import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input as AntInput, Modal, Select as AntSelect } from "antd";
import { ICategory } from "../../../types/api/category";
import { removeSpecialChars } from "../../../lib/utils";
import UploadImage from "../../components/UploadImage";

export interface CategoryFormValues {
  name: string;
  slug: string;
  status: boolean;
  parentId?: string | null;
  categoryType?: string | null;
}

export const CATEGORY_TYPE_OPTIONS = [
  { label: "Sculptures", value: "SCULPTURES" },
  { label: "Human Bust", value: "HUMAN_BUST" },
  { label: "Abstract", value: "ABSTRACT" },
  { label: "Animal", value: "ANIMAL" },
];

interface CategoryFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CategoryFormValues) => void;
  submitting?: boolean;
  editingCategory?: ICategory | null;
  /** Set when creating/editing a subcategory, so it saves scoped to that parent */
  parentId?: string | null;
  parentName?: string;
}

const emptyValues: CategoryFormValues = {
  name: "",
  slug: "",
  status: true,
  parentId: null,
  categoryType: null,
};

export const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  editingCategory,
  parentId,
  parentName,
}) => {
  const { control, handleSubmit, reset, setValue, watch } =
    useForm<CategoryFormValues>({
      defaultValues: emptyValues,
    });

  // Once the user types their own slug, stop auto-generating it from the name
  const slugManuallyEdited = useRef(false);
  const isChild = !!parentId;

  useEffect(() => {
    if (!open) return;

    slugManuallyEdited.current = !!editingCategory?.slug;

    if (editingCategory) {
      reset({
        name: editingCategory.name ?? "",
        slug: editingCategory.slug ?? "",
        status: editingCategory.status ?? true,
        parentId: editingCategory.parentId ?? parentId ?? null,
        categoryType: editingCategory.categoryType ?? null,
      });
    } else {
      reset({ ...emptyValues, parentId: parentId ?? null });
    }
  }, [open, editingCategory, parentId, reset]);

  const nameValue = watch("name");

  useEffect(() => {
    if (!slugManuallyEdited.current) {
      setValue("slug", removeSpecialChars(nameValue || ""));
    }
  }, [nameValue, setValue]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      title={
        editingCategory
          ? "Edit Category"
          : isChild
            ? `Add Subcategory${parentName ? ` (${parentName})` : ""}`
            : "Add Category"
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">

        {/* Name */}
        <Controller
          name="name"
          control={control}
          rules={{ required: "Please enter a name" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <AntInput
                {...field}
                placeholder="e.g. Sculpture"
                status={fieldState.error ? "error" : ""}
              />
              {fieldState.error && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Slug — auto-filled from name, editable */}
        <Controller
          name="slug"
          control={control}
          rules={{ required: "Slug is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Slug</label>
              <AntInput
                {...field}
                placeholder="auto-generated-from-name"
                status={fieldState.error ? "error" : ""}
                onChange={(e) => {
                  slugManuallyEdited.current = true;
                  field.onChange(removeSpecialChars(e.target.value));
                }}
              />
              {fieldState.error && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Status */}
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <AntSelect
                value={field.value}
                onChange={field.onChange}
                options={[
                  { label: "Active", value: true },
                  { label: "Inactive", value: false },
                ]}
              />
            </div>
          )}
        />

        {/* Category Type */}
        <Controller
          name="categoryType"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Category Type
              </label>
              <AntSelect
                value={field.value ?? null}
                onChange={field.onChange}
                allowClear
                placeholder="Select category type"
                options={[
                  { label: "Unassigned", value: null },
                  ...CATEGORY_TYPE_OPTIONS,
                ]}
              />
            </div>
          )}
        />

        {/* parentId rides along silently — this is what keeps a subcategory
            save scoped to its parent instead of showing up in the parent grid */}
        <Controller name="parentId" control={control} render={() => <></>} />

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-1.5 rounded-md bg-primary text-white disabled:opacity-60"
          >
            {editingCategory ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
